// Pure, axios-free sort/filter/progress helpers extracted from DraftBundleDashboard.tsx
// (lines ~218-279). These centralise the picked/required reduces, the sort comparator (incl. the
// progress divide-by-zero guard), and the search/status filter predicate so the draft-list ordering
// math is unit-testable. The component is owned by this workflow and is rewired to call these — the
// rendered order and filtered set are identical.

import { DraftBundle } from '../types';

export type DraftSortKey = 'name' | 'status' | 'progress' | 'updated';
export type SortDir = 'asc' | 'desc';
export type DraftStatusFilter = 'all' | 'picked' | 'partially_picked';

// Fixed status rank so the statuses group deterministically when sorting by status.
export const STATUS_RANK: Record<string, number> = { partially_picked: 0, picked: 1 };

// Total units picked across all child SKUs of a draft.
export const getTotalPickedItems = (draft: DraftBundle): number =>
  Object.values(draft.childSKUPicking).reduce((total, picking) => total + picking.totalPicked, 0);

// Total units required across all child SKUs of a draft.
export const getTotalRequiredItems = (draft: DraftBundle): number =>
  Object.values(draft.childSKUPicking).reduce((total, picking) => total + picking.totalRequired, 0);

// Pick progress as a 0..1 fraction, with the divide-by-zero guard (required 0 -> 0).
export const draftProgress = (draft: DraftBundle): number => {
  const req = getTotalRequiredItems(draft);
  return req > 0 ? getTotalPickedItems(draft) / req : 0;
};

// Direction-aware comparator for the four sort keys. Mirrors DraftBundleDashboard's inline compare:
// name uses a locale/numeric-aware compare, status uses STATUS_RANK, progress uses draftProgress,
// updated compares lastUpdated timestamps. The direction multiplier is applied to the whole result.
export const compareDrafts = (
  a: DraftBundle,
  b: DraftBundle,
  key: DraftSortKey,
  dir: SortDir,
): number => {
  const dirMul = dir === 'desc' ? -1 : 1;
  let base: number;
  switch (key) {
    case 'name':
      base = a.bundleSKU.name.localeCompare(b.bundleSKU.name, undefined, { numeric: true, sensitivity: 'base' });
      break;
    case 'status':
      base = (STATUS_RANK[a.status] ?? 0) - (STATUS_RANK[b.status] ?? 0);
      break;
    case 'progress':
      base = draftProgress(a) - draftProgress(b);
      break;
    case 'updated':
    default:
      base = new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
      break;
  }
  return base * dirMul;
};

// Search/status filter predicate. Search matches bundle name, and — only when `showOwner` (the admin
// cross-user view) — the owner name/email. Status filter narrows by draft.status ('all' = no filter).
export const matchesDraftFilter = (
  draft: DraftBundle,
  searchQuery: string,
  statusFilter: DraftStatusFilter,
  showOwner: boolean,
): boolean => {
  const q = searchQuery.trim().toLowerCase();
  const matchesStatus = statusFilter === 'all' || draft.status === statusFilter;
  const matchesSearch = !q
    || draft.bundleSKU.name.toLowerCase().includes(q)
    || (showOwner && (
      (draft.owner?.name ?? '').toLowerCase().includes(q)
      || (draft.owner?.email ?? '').toLowerCase().includes(q)
    ));
  return matchesStatus && matchesSearch;
};
